import Cookies from 'js-cookie';

import { FiAnalyticsJS, IWindow } from '../Window';
import store from '../reduxStore';
import { getTenancy } from '../../hooks/useTenancy';
import {
  series3CollarId,
  series3PlusCollarId,
  miniCollarId,
  ultraCollarId,
  f3BandId,
  martingaleBandId,
} from '../../types/Product';
import { getProductsAndVariantsBySku } from '../product';
import { SegmentCartEventProperties, SegmentProduct, SegmentPurchaseEventProperties } from './events';
import { showNanoVariant } from '../../components/NanoBanner/NanoBanner';
import { getQuizReferral, isQuizPreviewOverrideActive } from '../quizReferral';
import hotjar from './HotJar';
import { hasTargetingConsent } from '../consent';
import { getTrialLandingSource } from '../trialLandingSource';
import AppPaths from '../../AppPaths';
import { getCountryCodeFromCookie } from '../../contexts/LocaleContext';

// Product IDs tracked for marketing pixels (Northbeam, Facebook, etc.)
export const TRACKED_COLLAR_PRODUCT_IDS = [series3CollarId, series3PlusCollarId, miniCollarId, ultraCollarId];

/**
 * Accessories that exist as Meta catalog items and so can be retargeted. Anything not listed
 * here is filtered out before reaching the pixel, which is what we want — reporting a product
 * the catalog doesn't hold just produces unmatched ids in Events Manager.
 */
// Product.ts is copied from the web repo and has no constant for the charging base.
const chargingBaseS3Id = 'additional-base-s3';
const supplementProductIds = [
  'supplements-multivitamin',
  'supplements-skin-coat',
  'supplements-hip-joint',
  'supplements-calming',
];

export const TRACKED_ACCESSORY_PRODUCT_IDS = [f3BandId, martingaleBandId, chargingBaseS3Id, ...supplementProductIds];

/**
 * Check if the event should be tracked for marketing pixels based on the entire set of properties.
 */
export function shouldTrackMarketingEvent(
  properties: SegmentPurchaseEventProperties | SegmentCartEventProperties | SegmentProduct,
): boolean {
  // Don't track events for Verizon checkout
  const isVerizonCheckout = typeof window !== 'undefined' && window.location.pathname.includes(AppPaths.Verizon.Root);

  if (isVerizonCheckout) {
    return false;
  }

  const productsAndVariantsBySku = getProductsAndVariantsBySku(store.getState().config.products);

  // Don't track events that don't contain any products we care about.
  // ... for cart/purchase events with multiple products
  if ('products' in properties) {
    return properties.products.some((product: SegmentProduct) =>
      isTrackedMarketingProduct(product, productsAndVariantsBySku),
    );
  }

  // ... for single product events
  return isTrackedMarketingProduct(properties, productsAndVariantsBySku);
}

/**
 * Check if a segment product should be tracked for marketing pixels.
 * Returns true for tracked collar kits and their associated subscriptions.
 */
export function isTrackedMarketingProduct(
  segmentProduct: SegmentProduct,
  productsAndVariantsBySku: ReturnType<typeof getProductsAndVariantsBySku>,
): boolean {
  if (segmentProduct.isUpgrade) {
    return false;
  }

  const productAndVariant = productsAndVariantsBySku.get(segmentProduct.sku);
  if (!productAndVariant) {
    return false;
  }

  const { product } = productAndVariant;
  if (TRACKED_COLLAR_PRODUCT_IDS.includes(product.id) || TRACKED_ACCESSORY_PRODUCT_IDS.includes(product.id)) {
    return true;
  }

  return false;
}

// Copied from https://segment.com/docs/connections/spec/identify/#traits
interface ReservedIdentifyTraits {
  address?: {
    city?: string;
    country?: string;
    postalCode?: string;
    state?: string;
    street?: string;
  };
  age?: number;
  avatar?: string;
  birthday?: Date;
  company?: {
    name?: string;
    id?: string | number;
    industry?: string;
    employee_count?: number;
    plan?: string;
  };
  createdAt?: Date;
  description?: string;
  email?: string;
  firstName?: string;
  gender?: string;
  id?: string; // Unique ID in your database for a user
  lastName?: string;
  name?: string;
  phone?: string;
  title?: string;
  username?: string;
  website?: string;
}

// Any custom identify traits we want to send
/* eslint-disable-next-line */
interface FiIdentifyTraits {}

// The union of reserved traits & Fi traits
export type IdentifyTraits = ReservedIdentifyTraits & FiIdentifyTraits;

// Partial typing of the Segment context object, covering only the fields we explicitly enrich.
// Full spec: https://segment.com/docs/connections/spec/common/#context
interface SegmentContext extends Record<string, any> {
  traits?: IdentifyTraits;
  locale?: string;
  timezone?: string;
  location?: { country?: string };
}

export interface IAnalyticsBackend {
  identify(userId: string): void;
  identify(userId: string, traits?: IdentifyTraits): void;
  identify(userId: string, traits?: IdentifyTraits, options?: SegmentAnalytics.SegmentOpts): void;
  page(): void;
  track(name: string, properties: any): void;
  track(name: string, properties: any, options?: SegmentAnalytics.SegmentOpts): void;
  ready(callback: () => void): void;
  trackLink(element: HTMLElement, name: string, properties?: any): void;
  /** Reset anonymous ID */
  reset(): void;

  pause(): void;
  unpause(): void;

  onIdentify(callback: (userId: string, traits?: IdentifyTraits, options?: SegmentAnalytics.SegmentOpts) => void): void;
  onTrack(callback: (event: string, properties: any, messageId: string) => void): void;
  userOrAnonymousId(): string;
  userTraits(): IdentifyTraits;
}

interface ISegmentAnalyticsSource {
  analytics: FiAnalyticsJS;
}

function facebookPixelPropertiesFromCookies(): { fbc: string | undefined; fbp: string | undefined } {
  // These are cookies set by Facebook for their pixel & ad clicks
  // https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
  // We send these along so our analytics-backend can in turn send them to the Facebook Conversion API
  // Falls back to our self-captured fbclid value when _fbc cookie is missing
  // (ad blockers, no consent, Safari ITP can prevent fbevents.js from setting the cookie)
  const fbc = Cookies.get('_fbc') ?? store.getState().storeShop.cart.facebookClick;
  const fbp = Cookies.get('_fbp');

  return {
    fbc,
    fbp,
  };
}

function tikTokPixelPropertiesFromCookies(): { ttclid: string | undefined; ttp: string | undefined } {
  // These cookies are set by TikTok for their pixel & ad clicks
  // https://ads.tiktok.com/marketing_api/docs?id=1701890980108353
  // We send these along so our analytics-backend can in turn send them to the TikTok Events API
  const ttclid = Cookies.get('ttclid');
  const ttp = Cookies.get('_ttp');

  return {
    ttclid,
    ttp,
  };
}

function augmentProperties(properties: any) {
  let timezone: string | undefined;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    // Noop
  }

  // Check if this is a Verizon checkout or nano variant event
  const isVerizonCheckout = typeof window !== 'undefined' && window.location.pathname.includes(AppPaths.Verizon.Root);
  const isNanoVariant = showNanoVariant();

  // Quiz-referred session attribution rides on every event (incl. Product
  // Viewed and Order Completed) so purchase-per-quiz-click is measurable in
  // the warehouse without joining quiz session events.
  const quizReferral = getQuizReferral();
  const quizProperties = quizReferral
    ? {
        quiz_referred: true,
        quiz_recommended_product: quizReferral.recommendedProduct,
        quiz_cta_type: quizReferral.ctaType,
        // Lets the warehouse exclude QA sessions using the ?quizPdpPreview=1 override.
        ...(isQuizPreviewOverrideActive() ? { quiz_preview_override: true } : {}),
      }
    : {};

  return {
    ...properties,
    ...facebookPixelPropertiesFromCookies(),
    ...tikTokPixelPropertiesFromCookies(),
    ...quizProperties,
    timezone,
    // These flags will be used by the analytics backend to filter out server-side events that should not be tracked.
    isVerizonCheckout,
    isNanoVariant,
    trackingConsented: hasTargetingConsent(),
    country: getCountryCodeFromCookie(),
    language: Cookies.get('fi_language') ?? 'en-US',
    awinClick: store.getState().storeShop.cart.awinClick,
    redirectCohort: Cookies.get('fi_redirect_cohort'),
    // Attributes ATC/Order (and every other) events to the trial LP when the
    // session started there, so LP -> ATC / LP -> Order separate from the PDP.
    trialLandingSource: getTrialLandingSource(),
  };
}

abstract class AnalyticsWrapper {
  constructor(protected readonly backend: IAnalyticsBackend) {}
}

/**
 * Filters out events using the provided `filterFn`. Returning `false` from
 * `filterFn` skips emitting the event.
 */
export class FilterAnalytics extends AnalyticsWrapper implements IAnalyticsBackend {
  constructor(
    protected readonly backend: IAnalyticsBackend,
    /** Return false to skip event. */
    private readonly shouldExecute: <K extends keyof IAnalyticsBackend>(
      funcName: K,
      args: Parameters<IAnalyticsBackend[K]>[],
    ) => boolean,
  ) {
    super(backend);
  }

  identify(userId: string, traits?: IdentifyTraits, options?: SegmentAnalytics.SegmentOpts) {
    this.maybeExecute('identify', userId, traits, options);
  }

  page() {
    this.maybeExecute('page');
  }

  track(name: string, properties: any, options?: SegmentAnalytics.SegmentOpts) {
    this.maybeExecute('track', name, properties, options);
  }

  ready(callback: () => void) {
    this.backend.ready(callback);
  }

  trackLink(element: HTMLElement, name: string, properties?: any) {
    this.maybeExecute('trackLink', element, name, properties);
  }

  pause() {
    this.backend.pause();
  }

  unpause() {
    this.backend.unpause();
  }

  reset() {
    this.maybeExecute('reset');
  }

  onIdentify(
    callback: (userId: string, traits?: IdentifyTraits, options?: SegmentAnalytics.SegmentOpts) => void,
  ): void {
    this.backend.onIdentify(callback);
  }

  onTrack(callback: (event: string, properties: any, messageId: string) => void) {
    this.backend.onTrack(callback);
  }

  userOrAnonymousId() {
    return this.backend.userOrAnonymousId();
  }

  userTraits(): IdentifyTraits {
    return this.backend.userTraits();
  }

  private maybeExecute<K extends keyof IAnalyticsBackend>(name: K, ...args: Parameters<IAnalyticsBackend[K]>) {
    if (this.shouldExecute(name, args as any[])) {
      (this.backend[name] as (...fnArgs: any) => void).apply(this.backend, args);
    }
  }
}

export class QueuedAnalytics extends AnalyticsWrapper implements IAnalyticsBackend {
  private readonly queue: Array<{ name: keyof IAnalyticsBackend; args: any[] }>;
  private pauseCount: number;

  constructor(backend: IAnalyticsBackend) {
    super(backend);
    this.queue = [];
    this.pauseCount = 0;
  }

  public identify(userId: string, traits?: IdentifyTraits, options?: SegmentAnalytics.SegmentOpts) {
    this.queueOrExecute('identify', userId, traits, options);
  }

  public page(): void {
    this.queueOrExecute('page');
  }

  public track(name: string, properties: any, options?: SegmentAnalytics.SegmentOpts): void {
    this.queueOrExecute('track', name, properties, options);
  }

  public ready(callback: () => void) {
    this.backend.ready(callback);
  }

  public reset() {
    this.backend.reset();
  }

  public trackLink(element: HTMLElement, name: string, properties?: any) {
    // No queueing necessary
    this.backend.trackLink(element, name, properties);
  }

  public pause() {
    this.pauseCount++;
  }

  public unpause() {
    this.pauseCount--;
    if (this.pauseCount === 0) {
      this.flushQueue();
    }
  }

  public onIdentify(
    callback: (userId: string, traits?: IdentifyTraits, options?: SegmentAnalytics.SegmentOpts) => void,
  ): void {
    this.backend.onIdentify(callback);
  }

  public onTrack(callback: (event: string, properties: any, messageId: string) => void) {
    this.backend.onTrack(callback);
  }

  public userOrAnonymousId(): string {
    return this.backend.userOrAnonymousId();
  }

  public userTraits(): IdentifyTraits {
    return this.backend.userTraits();
  }

  private executeCall({ name, args }: { name: keyof IAnalyticsBackend; args: any[] }) {
    (this.backend[name] as (...fnArgs: any) => void).apply(this.backend, args);
  }

  private queueOrExecute<K extends keyof IAnalyticsBackend>(name: K, ...args: Parameters<IAnalyticsBackend[K]>) {
    const obj = { name, args };
    if (this.pauseCount === 0) {
      this.executeCall(obj);
    } else {
      this.queue.push(obj);
    }
  }

  private flushQueue() {
    while (this.queue.length > 0) {
      this.executeCall(this.queue.shift()!);
    }
  }
}

class SegmentAnalytics implements IAnalyticsBackend {
  private source: ISegmentAnalyticsSource;

  constructor(source: ISegmentAnalyticsSource) {
    this.source = source;
  }

  public identify(userId: string, traits?: IdentifyTraits, options?: SegmentAnalytics.SegmentOpts) {
    this.source.analytics.identify(userId, traits, this.augmentSegmentOptions(options));
  }

  public page(): void {
    this.source.analytics.page(augmentProperties({}), this.augmentSegmentOptions());
  }

  public track(name: string, properties: any, options?: SegmentAnalytics.SegmentOpts): void {
    this.source.analytics.track(name, augmentProperties(properties), this.augmentSegmentOptions(options));

    // Mirror track events to Hotjar so they show up as markers on session
    // replay timelines. Hotjar can't record inside cross-origin iframes
    // (e.g. Stripe Elements), so these events are the only visibility into
    // checkout progress and failures during replays. Mirroring here (behind
    // FilterAnalytics and QueuedAnalytics) keeps impersonation/test-tenancy
    // filtering and consent queueing intact.
    hotjar.event(name);
  }

  public trackLink(element: HTMLElement, name: string, properties: any = {}) {
    this.source.analytics.trackLink(element, name, augmentProperties(properties));
  }

  public ready(callback: () => void) {
    this.source.analytics.ready(callback);
  }

  public reset() {
    this.source.analytics.reset();
  }

  public pause() {
    // Noop
  }

  public unpause() {
    // Noop
  }

  public onIdentify(
    callback: (userId: string, traits?: IdentifyTraits, options?: SegmentAnalytics.SegmentOpts) => void,
  ) {
    this.source.analytics.off?.('identify', callback);
    this.source.analytics.on('identify', callback);
  }

  public onTrack(trackCallback: (event: string, properties: any, messageId: string) => void) {
    // analytics.js does emit a 'track' event, but it doesn't include the original Track instance that it
    // built. We want that so we can use the same messageId. The 'invoke' event does include the Track instance.
    // The type definitions for `on` defines the first argument as a string but it depends on the event.
    // For the invoke event, the first argument is a Track, Identify, or Page object.
    const invokeCallback = (facade: any) => {
      if (facade.constructor.name === 'Track') {
        const trackObj = facade.obj;
        trackCallback(trackObj.event, trackObj.properties, trackObj.messageId);
      }
    };

    this.source.analytics.off?.('invoke', invokeCallback);
    this.source.analytics.on('invoke', invokeCallback);
  }

  public userOrAnonymousId(): string {
    // This is not always defined
    if (typeof this.source.analytics.user !== 'function') {
      return '';
    }

    return this.source.analytics.user().id() || this.source.analytics.user().anonymousId();
  }

  public userTraits(): IdentifyTraits {
    // This is not always defined
    if (typeof this.source.analytics.user !== 'function') {
      return {};
    }

    // The segment types are incorrect on this, you can access traits in this fashion, just
    // run `analytics.user().traits()` in the ecommerce site's browser console to verify
    // Also in code: https://github.com/segmentio/analytics.js/blob/7dda4259d40375666ea6fd166d51b208d79c38ec/analytics.js#L5040
    return this.source.analytics.user().traits() as unknown as IdentifyTraits;
  }

  private augmentSegmentOptions(options?: SegmentAnalytics.SegmentOpts) {
    try {
      const baseContext = (options?.context ?? {}) as SegmentContext;

      // Enrich context with locale, timezone, and country.
      // Existing values from baseContext take precedence via nullish coalescing.
      const locale =
        baseContext.locale ??
        (
          Cookies.get('fi_language') ??
          (typeof navigator !== 'undefined' ? navigator.language : undefined) ??
          'en-US'
        ).replace('_', '-');

      let timezone = baseContext.timezone;
      if (!timezone) {
        try {
          timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch {
          // Intl may be unavailable in some environments
        }
      }

      const country = baseContext.location?.country ?? getCountryCodeFromCookie();

      const enrichedContext: SegmentContext = {
        ...baseContext,
        locale,
        timezone,
        location: { ...baseContext.location, country },
      };

      // Merge user traits into context if available.
      // Context traits (caller-supplied) take precedence over stored user traits.
      if (typeof this.source.analytics.user === 'function') {
        const userTraits = this.source.analytics.user().traits() as unknown as IdentifyTraits;
        if (userTraits && Object.keys(userTraits).length > 0) {
          const { traits: contextTraits } = enrichedContext;
          enrichedContext.traits = contextTraits ? { ...userTraits, ...contextTraits } : userTraits;
        }
      }

      return { ...options, context: enrichedContext };
    } catch (e) {
      // Context enrichment must never break analytics event emission.
      // Fall back to passing options through unmodified.
      console.warn('augmentSegmentOptions failed; falling back to original options', e);
      return options;
    }
  }
}

// tslint:disable:no-console
class LogAnalytics implements IAnalyticsBackend {
  private identifyCallback?: (userId: string, traits?: IdentifyTraits, options?: SegmentAnalytics.SegmentOpts) => void;
  private trackCallback?: (event: string, properties: any, messageId: string) => void;
  private userId = '';
  private traits: IdentifyTraits = {};

  public identify(userId: string, traits?: IdentifyTraits, options?: SegmentAnalytics.SegmentOpts) {
    console.log('Identifying user as: ', userId, traits, options);
    this.userId = userId;
    this.traits = traits ?? {};
    this.identifyCallback?.(userId, traits, options);
  }

  public page(): void {
    console.log('Analytics: page');
  }

  public reset(): void {
    console.log('Analytics: reset');
  }

  public track(name: string, properties: any, options?: SegmentAnalytics.SegmentOpts): void {
    console.log(
      'Analytics: track - name: ',
      name,
      ', properties: ',
      augmentProperties(properties),
      ', options: ',
      options,
    );

    // For logging-only analytics, we don't really care about a message id
    const messageId = '';
    this.trackCallback?.(name, properties, messageId);
  }

  public ready(callback: () => void) {
    setTimeout(() => {
      callback();
    }, 0);
  }

  public trackLink(_element: HTMLElement, _name: string, _properties?: any) {
    // Noop
  }

  public pause() {
    // Noop
  }

  public unpause() {
    // Noop
  }

  public onIdentify(
    callback: (userId: string, traits?: IdentifyTraits, options?: SegmentAnalytics.SegmentOpts) => void,
  ) {
    this.identifyCallback = callback;
  }

  public onTrack(callback: (event: string, properties: any, messageId: string) => void) {
    this.trackCallback = callback;
  }

  public userOrAnonymousId(): string {
    return this.userId;
  }

  public userTraits(): IdentifyTraits {
    return this.traits;
  }
}

function filterAnalyticsEvents<K extends keyof IAnalyticsBackend>(
  _funcName: K,
  _args: Parameters<IAnalyticsBackend[K]>[],
): boolean {
  const session = store.getState().session;
  const tenancy = getTenancy();
  if (session?.impersonating) {
    console.debug('Filtering out analytics event due to impersonation.');
    return false;
  } else if (tenancy && tenancy === 'test') {
    console.debug('Filtering out analytics event due to non-prod tenancy.');
    return false;
  } else {
    return true;
  }
}

// tslint:enable:no-console

const extendedWindow: IWindow = window;
const analytics = extendedWindow.analytics
  ? new FilterAnalytics(
      new QueuedAnalytics(new SegmentAnalytics(extendedWindow as ISegmentAnalyticsSource)),
      filterAnalyticsEvents,
    )
  : new LogAnalytics();

export default analytics;
